import { useState } from 'react';
import type { ChatProductItem } from '../../providers/types';

interface ProductCardMessageProps {
  products: ChatProductItem[];
  isOwn: boolean;
  onProductClick?: (productId: number, variantId?: number) => void;
}

function ProductCard({ product, isOwn, onProductClick }: {
  product: ChatProductItem;
  isOwn: boolean;
  onProductClick?: (productId: number, variantId?: number) => void;
}) {
  const [imgError, setImgError] = useState(false);

  const handleCardClick = () => {
    if (onProductClick) {
      onProductClick(Number(product.id), product.variantId);
    }
  };

  return (
    <div
      className={`flex-shrink-0 w-40 rounded-xl overflow-hidden border hover:border-brand-gold/40 transition-colors ${
        isOwn
          ? 'bg-white/10 border-white/10'
          : 'bg-brand-navy/5 border-brand-navy/10'
      } ${onProductClick ? 'cursor-pointer' : ''}`}
      onClick={onProductClick ? handleCardClick : undefined}
      role={onProductClick ? 'button' : undefined}
      tabIndex={onProductClick ? 0 : undefined}
      onKeyDown={onProductClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      } : undefined}
    >
      {/* Product Image */}
      <div className={`w-full h-28 flex items-center justify-center overflow-hidden ${isOwn ? 'bg-white/5' : 'bg-brand-navy/5'}`}>
        {product.imageUrl && !imgError ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${isOwn ? 'bg-white/5' : 'bg-brand-navy/5'}`}>
            <span className="text-3xl font-serif text-brand-gold/60 uppercase">
              {product.name.charAt(0)}
            </span>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-2 space-y-1">
        <p className={`font-serif text-xs leading-tight line-clamp-2 ${isOwn ? 'text-white' : 'text-brand-navy'}`} title={product.name}>
          {product.name}
        </p>
        <p className="text-brand-gold text-xs font-medium">
          {product.price !== null && product.price !== '' ? product.price : 'Price on request'}
        </p>
      </div>
    </div>
  );
}

export function ProductCardMessage({ products, isOwn, onProductClick }: ProductCardMessageProps) {
  if (!products || products.length === 0) return null;

  return (
    <div className={`flex gap-2 overflow-x-auto pb-1 scrollbar-thin scrollbar-track-transparent ${isOwn ? 'scrollbar-thumb-white/20' : 'scrollbar-thumb-brand-navy/20'}`}>
      {products.map((product, index) => (
        <ProductCard key={product.id ?? index} product={product} isOwn={isOwn} onProductClick={onProductClick} />
      ))}
    </div>
  );
}
